import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";

import AgendaTech from "../components/Agenda/AgendaTech";
import AgendaEquipe from "../components/Agenda/AgendaEquipe";

import useUsers from "../hooks/useUsers";
import useFetchPersonnels from "../hooks/useFetchPersonnels";
import useFetchDeclarations from "../hooks/useFetchDeclarations";
import useFetchSites from "../hooks/useFetchSites";
import { useAuth } from "../contexts/AuthContext";
import { useEnums } from "../contexts/EnumContext";

import config from "../config/config";
import authHeader from "../config/authHeader";

function Agenda() {
	const { enums } = useEnums();
	const { tab } = useParams();
	const isInterventionTab = false;//tab === "intervention";

	const [mode, setMode] = useState("personal");
	const tabs = ["Personnel", "Équipe"];

	// ---------------- helpers ----------------
	const now = new Date();
	const todayAt = (h, m = 0, s = 0) =>
		new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, s, 0);

	const isZeroDate = (s) => !s || s === "0000-00-00" || s === "00-00-0000";
	const pad2 = (n) => String(n).padStart(2, "0");

	// seconds -> {h,m}
	const secToHM = (sec = 0) => ({
		h: Math.floor(Number(sec || 0) / 3600),
		m: Math.floor((Number(sec || 0) % 3600) / 60),
	});

	// ✅ supports BOTH YYYY-MM-DD and DD-MM-YYYY (your backend sends DD-MM-YYYY for interventions)
	const parseYMDorDMY = (s) => {
		const str = String(s || "").trim();
		if (!str) return null;

		// YYYY-MM-DD
		if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
			const [y, m, d] = str.split("-").map(Number);
			return { y, m, d };
		}

		// DD-MM-YYYY
		if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
			const [d, m, y] = str.split("-").map(Number);
			return { y, m, d };
		}

		return null;
	};

	const makeDateTimeSafe = (dateStr, h = 0, m = 0, s = 0) => {
		const parts = parseYMDorDMY(dateStr);
		if (!parts) return null;
		return new Date(parts.y, parts.m - 1, parts.d, h, m, s, 0);
	};

	const makeDateTimeFromDateAndSec = (dateStr, sec) => {
		const total = Number(sec ?? 0);
		const { h, m } = secToHM(total);
		const d = makeDateTimeSafe(dateStr, h, m, total % 60);
		return d;
	};
	// --------------------------------------------------------

	const [loadingRDV, setLoadingRDV] = useState(false);
	const [lstRdv, setLstRdv] = useState([]);
	const [lstIntervention, setLstIntervention] = useState([]);

	const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
	const [groupName, setGroupName] = useState("");
	const [groups, setGroups] = useState([]);

	const { users, fetchUsers } = useUsers();
	const { personnels, fetchPersonnels } = useFetchPersonnels();
	const { declarations, fetchDeclarations } = useFetchDeclarations();
	const { sites, fetchSites } = useFetchSites();

	// ✅ prevent StrictMode double-run
	const bootstrapDoneRef = useRef(false);
	useEffect(() => {
		if (bootstrapDoneRef.current) return;
		bootstrapDoneRef.current = true;

		fetchUsers();
		fetchPersonnels();
		fetchDeclarations();
		fetchSites();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// sync local groups from enums
	useEffect(() => {
		setGroups(enums?.["nomGroupe"] || []);
	}, [enums]);

	// ---------------- Expertises (non-intervention tab) ----------------
	const fetchExpertises = useCallback(async () => {
		setLoadingRDV(true);
		try {
			const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getExpertises`, {
				method: "GET",
				headers: authHeader(),
			});
			const data = await response.json();
			setLstRdv(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error("Erreur lors de la récupération des rendez-vous:", error);
			setLstRdv([]);
		} finally {
			setLoadingRDV(false);
		}
	}, []);

	useEffect(() => {
		if (isInterventionTab) return;
		fetchExpertises();
	}, [isInterventionTab, fetchExpertises]);

	const handleEventsChanged = useCallback(() => {
		if (isInterventionTab) return;
		fetchExpertises();
	}, [isInterventionTab, fetchExpertises]);

	const handleTabClick = (t) => setMode(t === "Personnel" ? "personal" : "team");

	const USERS = useMemo(() => {
		return (users ?? []).map((u) => ({
			Name: `${u?.Nom ?? ""}`.trim() || "Sans nom",
			UserUUID: String(u?.UUID_ ?? ""),
			Color: u?.color ?? "#FF5733",
			Role: u?.role ?? "",
			Charge_Affaire: Boolean(u?.Charge_Affaire),
		}));
	}, [users]);

	const findUserIdByCA = useCallback(
		(ca) => {
			if (!USERS.length) return undefined;
			if (!ca) return "-1";

			const firstBloc = ca.split("-")[0].trim().toLowerCase();
			const firstNameToken = firstBloc.split(/\s+/)[0];

			const match = USERS.find((u) => {
				const fullName = `${u?.Name ?? ""}`.toLowerCase();
				return fullName.includes(firstBloc) || fullName.includes(firstNameToken);
			});

			return match?.UserUUID ?? "-1";
		},
		[USERS]
	);

	const EVENTS = useMemo(() => {
		if (isInterventionTab) return [];
		if (!Array.isArray(lstRdv)) return [];

		const ONE_HOUR = 60 * 60 * 1000;

		const parseEnd = (rdv, start, startYmd) => {
			const endYmd = !isZeroDate(rdv?.Date_fin) ? rdv.Date_fin : startYmd;
			const endSec = Number(rdv?.Heure_fin ?? 0);
			const endCandidate = endYmd ? makeDateTimeFromDateAndSec(endYmd, endSec) : null;

			let end =
				endCandidate instanceof Date && !isNaN(endCandidate)
					? endCandidate
					: new Date(start.getTime() + ONE_HOUR);

			if (!(end instanceof Date) || isNaN(end) || end <= start) {
				end = new Date(start.getTime() + ONE_HOUR);
			}
			return end;
		};

		return lstRdv.map((rdv, idx) => {
			const ymd =
				!isZeroDate(rdv?.Date_Convoc)
					? rdv.Date_Convoc
					: !isZeroDate(rdv?.Date_Rdv)
						? rdv.Date_Rdv
						: null;

			const startSec = Number(rdv?.Heure_Convoc || rdv?.Heure_Rdv || 3600 * 9);
			const { h, m } = secToHM(startSec);
			const startCandidate = ymd ? makeDateTimeSafe(ymd, h, m, startSec % 60) : null;

			const start =
				startCandidate instanceof Date && !isNaN(startCandidate) ? startCandidate : todayAt(9, 0);

			const end = parseEnd(rdv, start, ymd);

			return {
				Id: rdv?.UUID_ || `tmp-${idx}`,
				Subject: rdv?.Titre || rdv?.Type_Document || "Rendez-vous",
				StartTime: start,
				EndTime: end,
				Description: rdv?.Resume || "",
				Location: rdv?.Four_Nom || "",
				UserUUID: findUserIdByCA(rdv?.CA),
				declarationUUID: rdv?.UUID_Declaration,
				siteUUID: rdv?.UUID_Site_,
			};
		});
	}, [isInterventionTab, lstRdv, findUserIdByCA]);

	// ---------------- Interventions (intervention tab) ----------------
	const fetchRDVIntervention = useCallback(async () => {
		try {
			const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getRDVGroupe`, {
				method: "GET",
				headers: authHeader(),
			});
			const data = await response.json();
			setLstIntervention(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error("Erreur lors de la récupération des interventions:", error);
			setLstIntervention([]);
		}
	}, []);

	useEffect(() => {
		if (!isInterventionTab) return;
		fetchRDVIntervention();
	}, [isInterventionTab, fetchRDVIntervention]);

	const INTERVENTIONS = useMemo(() => {
		if (!isInterventionTab) return [];
		if (!Array.isArray(lstIntervention)) return [];

		const ONE_HOUR = 60 * 60 * 1000;

		return lstIntervention.map((itv, idx) => {
			const startDateStr = itv?.Date_Debut ?? null;
			const endDateStr = itv?.Date_Fin ?? itv?.Date_Debut ?? null;

			const startSec = Number(itv?.Heure_Debut ?? 0);
			const endSec = Number(itv?.Heure_Fin ?? 0);

			const { h: sh, m: sm } = secToHM(startSec);
			const startCandidate = startDateStr
				? makeDateTimeSafe(startDateStr, sh, sm, startSec % 60)
				: null;

			const start =
				startCandidate instanceof Date && !isNaN(startCandidate) ? startCandidate : todayAt(9, 0);

			const endCandidate = endDateStr ? makeDateTimeFromDateAndSec(endDateStr, endSec) : null;

			const end =
				endCandidate instanceof Date && !isNaN(endCandidate)
					? endCandidate
					: new Date(start.getTime() + ONE_HOUR);

			const finalEnd = end > start ? end : new Date(start.getTime() + ONE_HOUR);

			const personnelArr = Array.isArray(itv?.UUID_Personnel?.uuidPersonnel)
				? itv.UUID_Personnel.uuidPersonnel.map(String)
				: [];

			return {
				Id: itv?.UUID_ || `itv-${idx}`,
				Subject: itv?.Titre || "Intervention",
				StartTime: start,
				EndTime: finalEnd,
				Description: "",
				siteUUID: String(itv?.UUID_Site ?? ""),
				groupeName: String(itv?.Groupe ?? ""),
				personnelUUID: personnelArr.length ? personnelArr : ["-1"],
			};
		});
	}, [isInterventionTab, lstIntervention]);

	// ---------------- modal / groups ----------------
	const openGroupModal = () => {
		setGroupName("");
		setIsGroupModalOpen(true);
	};
	const closeGroupModal = () => setIsGroupModalOpen(false);

	const headersForFormData = () => {
		const h = { ...(authHeader?.() ?? {}) };
		delete h["Content-Type"];
		delete h["content-type"];
		return h;
	};

	const submitGroupModal = (e) => {
		e?.preventDefault();
		const name = groupName.trim();
		if (!name) return;

		const formData = new FormData();
		formData.append("nom", name);

		fetch(`${config.apiBaseUrl}/4DACTION/KST_createGroupName`, {
			method: "POST",
			headers: headersForFormData(),
			body: formData,
		})
			.then((r) => r.json())
			.catch((err) => console.error("Erreur lors de la création du nom de groupe:", err));

		setGroups((prev) => (prev.includes(name) ? prev : [...prev, name])); // optimistic
		setIsGroupModalOpen(false);
	};

	// ---------------- filtering for AgendaTech ----------------
	const { user } = useAuth();
	const userUUID = user?.UUID_;

	const USERS_NORM = useMemo(() => USERS.map((u) => ({ ...u, UserUUID: String(u.UserUUID) })), [USERS]);
	const EVENTS_NORM = useMemo(() => EVENTS.map((e) => ({ ...e, UserUUID: String(e.UserUUID) })), [EVENTS]);

	const filteredUsers =
		mode === "personal" ? USERS_NORM.filter((u) => u.UserUUID === String(userUUID)) : USERS_NORM;

	const filteredEvents =
		mode === "personal" ? EVENTS_NORM.filter((e) => e.UserUUID === String(userUUID)) : EVENTS_NORM;

	return (
		<div className="z-10 flex-1 p-10">
			<div className="gap-6 grid grid-cols-12">
				<div className="gap-6 grid grid-cols-12 col-span-12 xxl:col-span-9">
					<div className="col-span-12 mt-6">
						<h1 className="mb-5 w-full font-bold text-gray-800 text-4xl text-center">Agenda</h1>

						{isInterventionTab ? (
							<>
								<div className="flex items-center justify-end mb-3">
									<button
										type="button"
										onClick={openGroupModal}
										className="bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded-lg text-white font-semibold transition"
									>
										Créer un groupe
									</button>
								</div>

								<div className="bg-white p-4 rounded-lg">
									<AgendaEquipe
										sites={sites}
										enumsNomGroupe={groups}
										personnel={personnels}
										events={INTERVENTIONS}
									/>
								</div>

								{isGroupModalOpen && (
									<div className="fixed inset-0 z-[999] flex items-center justify-center">
										<div className="absolute inset-0 bg-black/50" onClick={closeGroupModal} />
										<div className="relative w-[92%] max-w-md rounded-2xl bg-white shadow-xl p-6">
											<div className="flex items-start justify-between mb-4">
												<h3 className="text-lg font-bold text-gray-900">Créer un groupe</h3>
												<button
													type="button"
													onClick={closeGroupModal}
													className="text-gray-400 hover:text-gray-700 transition"
													aria-label="Fermer"
												>
													✕
												</button>
											</div>

											<form onSubmit={submitGroupModal} className="space-y-4">
												<div>
													<label className="block text-sm font-semibold text-gray-700">
														Nom du groupe
													</label>
													<input
														autoFocus
														className="mt-1 w-full h-11 rounded-lg border px-3 outline-none focus:ring-2 focus:ring-orange-400"
														value={groupName}
														onChange={(e) => setGroupName(e.target.value)}
														placeholder="Ex: Équipe Nord"
													/>
												</div>

												<div className="flex justify-end gap-2">
													<button
														type="button"
														onClick={closeGroupModal}
														className="h-11 px-4 rounded-lg border text-gray-700 hover:bg-gray-50 transition"
													>
														Annuler
													</button>

													<button
														type="submit"
														disabled={!groupName.trim()}
														className="h-11 px-4 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
													>
														Créer
													</button>
												</div>
											</form>
										</div>
									</div>
								)}
							</>
						) : (
							<>
								<div className="flex flex-nowrap justify-center items-center space-x-2 bg-black/5 p-3 rounded-lg w-[350px] mb-2 mx-auto">
									{tabs.map((t) => {
										const isActive =
											(t === "Personnel" && mode === "personal") ||
											(t === "Équipe" && mode === "team");

										return (
											<button
												key={t}
												type="button"
												onClick={() => handleTabClick(t)}
												className={`py-3 px-10 text-sm font-semibold rounded-lg transition-all duration-500 ease-in-out
                          ${isActive
														? "bg-orange-600 border-b-2 border-orange-500 text-white animate-gradientWave"
														: "text-gray-600 bg-gray-100 hover:text-orange-400"
													}`}
											>
												{t}
											</button>
										);
									})}
								</div>

								<div className="bg-white p-4 rounded-lg">
									<AgendaTech
										users={filteredUsers}
										events={filteredEvents}
										sites={sites}
										declarations={declarations}
										onEventsChanged={handleEventsChanged}
										isPersonal={mode === "personal"}
										loading={loadingRDV}
									/>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default Agenda;
